/**
 * Feature Flag Redux Slice
 *
 * This slice manages feature flags for dynamic route access control.
 * It fetches feature configuration from the backend API and caches it per project.
 *
 * @author Sabya
 * @date September 30, 2025
 *
 * Key Features:
 * - Smart caching: API called only once per project
 * - Error resilience: Falls back to all features enabled on failure
 * - Deduplication: Prevents redundant API calls
 * - Supports 8 feature flags for different analysis components
 *
 * API Endpoint: GET /concept_testing/{project_id}/feature_configuration
 *
 * Expected API Response:
 * {
 *   "conceptAnalysis": true,
 *   "successFactors": false,
 *   "personaAnalysis": true,
 *   "comparingConcepts": true,
 *   "scores": false,
 *   "idealConcept": true,
 *   "purchase_drivers": true,  // Note: snake_case from API
 *   "radar": false
 * }
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { httpRequest } from '../../utils/http.util';

/**
 * Default Feature Configuration
 *
 * All features are enabled by default as a fallback mechanism.
 * This ensures the app remains functional even if the API fails.
 *
 * Feature Keys:
 * - conceptAnalysis: Concept-level analysis page
 * - successFactors: Success factors analysis
 * - personaAnalysis: Persona-specific analysis
 * - comparingConcepts: Concept comparison view
 * - scores: Scoring and metrics page
 * - idealConcept: Ideal concept builder
 * - purchaseDrivers: Purchase drivers analysis (future feature)
 * - radar: Radar chart visualization (future feature)
 */
const DEFAULT_FEATURES = {
    conceptAnalysis: true,
    successFactors: true,
    personaAnalysis: true,
    comparingConcepts: true,
    scores: true,
    idealConcept: true,
    purchaseDrivers: false,
    radar: false,
    scoresCsv: false,
};

/**
 * Testing Configuration
 *
 * Controls the configuration parameter in feature_configuration API call.
 * Values: 'default' | 'reckitt'
 * Set to null to omit the parameter from the API call.
 *
 * Usage:
 * - 'default': Test with default configuration
 * - 'reckitt': Test with reckitt-specific configuration
 * - null: Production mode (no configuration parameter)
 */
const TEST_CONFIGURATION = null; // Change to 'reckitt' or null or 'default' as needed

/**
 * Async Thunk: Fetch Feature Configuration
 *
 * Fetches feature flags from the backend API for a specific project.
 * Includes smart caching to prevent redundant API calls.
 *
 * @param {string} projectId - The project ID to fetch configuration for
 * @returns {Object} Object containing features and projectId
 *
 * Behavior:
 * 1. Checks if config already loaded for this project (deduplication)
 * 2. If yes: Returns cached config immediately
 * 3. If no: Makes API call to fetch fresh config
 * 4. On success: Returns mapped features
 * 5. On error: Falls back to DEFAULT_FEATURES (all enabled)
 *
 * API Call Frequency: Once per unique projectId
 *
 * Example Usage:
 *   dispatch(fetchFeatureConfig('project-123'))
 *
 * @author Sabya
 * @date September 30, 2025
 */
export const fetchFeatureConfig = createAsyncThunk(
    'features/fetchConfig',
    async (projectId, { rejectWithValue, getState }) => {
        // OPTIMIZATION: Check if already loaded for this project
        // This prevents redundant API calls when navigating between routes
        // within the same project
        const { loadedProjectId } = getState().features;
        if (loadedProjectId === projectId) {
            // Return cached features - no API call needed
            return { features: getState().features.features, projectId };
        }

        try {
            // Make API call to fetch feature configuration
            // Build URL with optional configuration parameter for testing
            const configParam = TEST_CONFIGURATION ? `?configuration=${TEST_CONFIGURATION}` : '';
            const response = await httpRequest({
                method: 'GET',
                url: `/concept_testing/${projectId}/feature_configuration${configParam}`,
                isAuthRequired: true,
            });

            const config = response.data || {};

            // Map API response to internal feature flags
            // Note: API uses snake_case for some fields (purchase_drivers)
            // while frontend uses camelCase consistently
            return {
                features: {
                    conceptAnalysis: config.conceptAnalysis ?? DEFAULT_FEATURES.conceptAnalysis,
                    successFactors: config.successFactors ?? DEFAULT_FEATURES.successFactors,
                    personaAnalysis: config.personaAnalysis ?? DEFAULT_FEATURES.personaAnalysis,
                    comparingConcepts: config.comparingConcepts ?? DEFAULT_FEATURES.comparingConcepts,
                    scores: config.scores ?? DEFAULT_FEATURES.scores,
                    idealConcept: config.idealConcept ?? DEFAULT_FEATURES.idealConcept,
                    purchaseDrivers: config.purchase_drivers ?? DEFAULT_FEATURES.purchaseDrivers, // API: snake_case
                    radar: config.radar ?? DEFAULT_FEATURES.radar,
                    scoresCsv: config.scores_csv ?? DEFAULT_FEATURES.scoresCsv, // API: snake_case
                },
                successFactors: config.success_factors || [],
                scoreFormat: config.score_format || 'categorical_colors',
                projectId,
            };
        } catch (error) {
            // ERROR HANDLING: Graceful degradation
            // If API fails, log warning but don't break the app
            // Return default features (all enabled) to ensure app remains functional
            console.warn('Failed to fetch feature config, using defaults:', error);
            return {
                features: DEFAULT_FEATURES,
                successFactors: [],
                scoreFormat: 'categorical_colors',
                projectId,
            };
        }
    }
);

/**
 * Feature Slice
 *
 * Redux Toolkit slice that manages feature flag state and reducers.
 *
 * State Structure:
 * {
 *   features: Object<string, boolean> - Feature flags (8 features)
 *   loading: boolean - API call in progress
 *   error: string | null - Error message if API fails
 *   loadedProjectId: string | null - Currently loaded project ID (for caching)
 * }
 *
 * @author Sabya
 * @date September 30, 2025
 */
const featureSlice = createSlice({
    name: 'features',
    initialState: {
        features: DEFAULT_FEATURES,      // Feature flags object
        successFactors: [],              // Success factors array from API
        scoreFormat: 'categorical_colors', // Score format from API
        loading: false,                  // API loading state
        error: null,                     // Error message
        loadedProjectId: null,           // Cached project ID
    },
    reducers: {
        /**
         * Update Features (Manual Override)
         *
         * Allows manual override of feature flags, useful for:
         * - Testing specific feature combinations
         * - Admin overrides
         * - Development/debugging
         *
         * Example:
         *   dispatch(updateFeatures({ conceptAnalysis: false, scores: true }))
         */
        updateFeatures: (state, action) => {
            state.features = { ...state.features, ...action.payload };
        },

        /**
         * Reset Features to Defaults
         *
         * Clears all cached data and resets to default state.
         * Useful when:
         * - Logging out
         * - Clearing cache
         * - Error recovery
         *
         * Example:
         *   dispatch(resetFeatures())
         */
        resetFeatures: (state) => {
            state.features = DEFAULT_FEATURES;
            state.successFactors = [];
            state.scoreFormat = 'categorical_colors';
            state.loading = false;
            state.error = null;
            state.loadedProjectId = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // PENDING: API call started
            .addCase(fetchFeatureConfig.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            // FULFILLED: API call succeeded
            .addCase(fetchFeatureConfig.fulfilled, (state, action) => {
                state.loading = false;
                state.features = action.payload.features;
                state.successFactors = action.payload.successFactors || [];
                state.scoreFormat = action.payload.scoreFormat || 'categorical_colors';
                state.loadedProjectId = action.payload.projectId;  // Cache project ID
            })
            // REJECTED: API call failed (should rarely happen due to try-catch in thunk)
            .addCase(fetchFeatureConfig.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch feature config';
                // Graceful degradation: Use default features even on error
                state.features = DEFAULT_FEATURES;
            });
    },
});

// ============================================================
// ACTIONS
// ============================================================

/**
 * Exported Actions
 *
 * - updateFeatures: Manually override feature flags
 * - resetFeatures: Reset to default state
 */
export const { updateFeatures, resetFeatures } = featureSlice.actions;

// ============================================================
// SELECTORS
// ============================================================

/**
 * Select all features
 * @returns {Object} All feature flags
 */
export const selectFeatures = (state) => state.features.features;

/**
 * Select loading state
 * @returns {boolean} True if API call in progress
 */
export const selectLoading = (state) => state.features.loading;

/**
 * Select error message
 * @returns {string | null} Error message or null
 */
export const selectError = (state) => state.features.error;

/**
 * Select loaded project ID
 * @returns {string | null} Currently cached project ID
 */
export const selectLoadedProjectId = (state) => state.features.loadedProjectId;

/**
 * Check if a specific feature is enabled
 *
 * @param {string} featureName - Name of the feature to check
 * @returns {function} Selector function that returns boolean
 *
 * Example Usage:
 *   const isEnabled = useSelector(selectIsFeatureEnabled('conceptAnalysis'))
 */
export const selectIsFeatureEnabled = (featureName) => (state) =>
    state.features.features[featureName] ?? false;

/**
 * Select success factors array
 * @returns {Array<string>} Success factors array from API response
 */
export const selectSuccessFactors = (state) => state.features.successFactors;

/**
 * Select score format
 * @returns {string} Score format from API response
 */
export const selectScoreFormat = (state) => state.features.scoreFormat;

// ============================================================
// DEFAULT EXPORT
// ============================================================

/**
 * Feature Reducer
 * Export for Redux store configuration
 */
export default featureSlice.reducer;